﻿import { FC, ReactNode, useEffect, useRef, useState } from "react";
import { Box, BoxProps, Flex } from "@chakra-ui/react";
import { getBottomPadding, getDistanceFromTop, getGap, getHeightPlusMargins, getVerticalMargins } from "../../util";

import "./ag-theme.css";

type AgGridContainerProps = {
    before?: ReactNode;
    gap?: string | number;
    hideGrid?: boolean | undefined;
    minGridHeight?: number;
    children?: ReactNode;
} & BoxProps;

/**
 * Use this component to wrap an AgGridReact component. It will automatically calculate the optimal height of the grid so that it fills the remaining space
 * available in the viewport. In order for this to work as expected, this must be the only element inside it's parent. You can have an element above it by
 * using the above prop.
 *
 * This component is especially helpful when using infinite scrolling. For infinite scrolling to work, the grid must have a fixed height and there has to
 * be a scroll bar on the grid itself.
 *
 * @param before - this must be a single top-level dom element. It will not work properly if a fragment with multiple top-level elements is provided.
 * @param gap - this is the space between the above element and the grid. It defaults to 3rem.
 * @param hideGrid - this is used to hide the grid. It defaults to false.
 * @param minGridHeight - this is the minimum height of the grid. It defaults to 200px.
 * @param children - this is the AgGridReact component that you want to wrap.
 * @constructor
 */
export const AgGridContainer: FC<AgGridContainerProps> = ({ before, gap = "3rem", hideGrid = false, minGridHeight = 200, children, ...boxProps }) => {
    // ---- Code to Calculate the optimal height of the data table ----
    const aboveContainerRef = useRef<HTMLDivElement>(null);
    const gridContainerRef = useRef<HTMLDivElement>(null);
    const [gridHeight, setGridHeight] = useState<string>(before ? `${minGridHeight}px` : "100%");
    useEffect(() => {
        if (before) {
            const calculateGridHeight = () => {
                const viewPortHeight = window.innerHeight;
                const parentPaddingBottom = getBottomPadding(gridContainerRef.current?.parentElement?.parentElement);
                const wrapperDistanceFromTop = getDistanceFromTop(gridContainerRef.current?.parentElement);
                const wrapperPaddingBottom = getBottomPadding(gridContainerRef.current?.parentElement);
                const wrapperGap = getGap(gridContainerRef.current?.parentElement);
                const heightOfBeforeElement = getHeightPlusMargins(aboveContainerRef.current);
                const gridContainerMarginsY = getVerticalMargins(gridContainerRef.current);

                const newHeight = Math.max(
                    minGridHeight,
                    viewPortHeight -
                        parentPaddingBottom -
                        wrapperDistanceFromTop -
                        wrapperPaddingBottom -
                        heightOfBeforeElement -
                        gridContainerMarginsY -
                        wrapperGap,
                );
                setGridHeight(`${newHeight}px`);
            };

            calculateGridHeight();
            window.addEventListener("resize", calculateGridHeight);
        }
    }, [before, minGridHeight]);

    return (
        <Flex flexDirection={"column"} height={"100%"} gap={gap}>
            {before && <Box ref={aboveContainerRef}>{before}</Box>}
            <Box ref={gridContainerRef} flexGrow={1}>
                {!hideGrid && (
                    <Box
                        height={gridHeight}
                        border={".8px solid var(--chakra-colors-chakra-border-color)"}
                        borderRadius={"0.5rem"}
                        overflow="hidden"
                        className="ag-theme-alpine"
                        {...boxProps}
                    >
                        {children}
                    </Box>
                )}
            </Box>
        </Flex>
    );
};
